import React, { useCallback } from 'react';
import { HtmlSanitized } from 'components/display/HtmlSanitized';
import { GuaranteeDto } from 'api/pricing';
import { ContractFunnel } from '../../types/interfaces';
import { GuaranteeCardItem } from './GuaranteeCardItem';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { useDialog } from 'hooks/useDialog';
import { Sizes } from 'components/forms/NumberSelector';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { useContractGuarantees } from '../../hooks/useContractGuarantees';

interface ContractGuaranteesProps {
  contractFunnel: ContractFunnel;
}

export const ContractGuarantees: React.FC<ContractGuaranteesProps> = ({ contractFunnel }) => {
  const { t } = useTranslation(NsKey.Pricing);
  const { openDialog } = useDialog();

  const {
    updateDeductible,
    updateCeiling,
    getGuaranteeValue,
    isActive,
    toggleGuarantee
  } = useContractGuarantees({ contractFunnel });

  const showGuaranteeDetails = useCallback(
    (title: string, longDescription: string) => {
      openDialog({
        title,
        size: Sizes["4xl"],
      },
        <HtmlSanitized html={longDescription} className="font-12 text-dark-light w-100" />
      );
    }, [openDialog]
  );

  const renderGuaranteeCard = useCallback((guarantee: GuaranteeDto) => {
    const values = getGuaranteeValue(guarantee.id);

    return (
      <GuaranteeCardItem
        key={guarantee.id}
        isActive={isActive(guarantee.id)}
        guarantee={guarantee}
        isRequired={guarantee.isRequired}
        toggleGuarantee={() => !guarantee.isRequired && toggleGuarantee(guarantee)}
        showGuaranteeDetails={showGuaranteeDetails}
        deductible={values.deductible}
        ceiling={values.ceiling}
        onDeductibleChange={(value) => updateDeductible(guarantee.id, value)}
        onCeilingChange={(value) => updateCeiling(guarantee.id, value)}
      />
    );
  }, [getGuaranteeValue, isActive, toggleGuarantee, updateDeductible, updateCeiling, showGuaranteeDetails]);

  return (
    <div className='flex flex-col gap-4'>
      <CardsSection title={t('PRICING.GUARANTEES.MANDATORY')}>
        {contractFunnel.guaranteesReferential.mandatory.map(renderGuaranteeCard)}
      </CardsSection>
      {contractFunnel.guaranteesReferential.optional.length > 0 && (
        <CardsSection title={t('PRICING.GUARANTEES.OPTIONAL')}>
          {contractFunnel.guaranteesReferential.optional.map(renderGuaranteeCard)}
        </CardsSection>
      )}
    </div>
  );
};
